import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance.js";
import Pagination from "../../common/Pagination/index.js";
import Loader from "../../common/Loader/index.js";
import { BiSolidEditAlt } from "react-icons/bi";
import { MdClose, MdDeleteForever } from "react-icons/md";
import Model from "../../common/Model/index.js";
import { LiaSearchPlusSolid } from "react-icons/lia";
import { notify, Toast } from "../../common/Toast/index.js";
import FormFields from "../../common/FormFields/index.js";
import { getUserMenuPermission } from "../../common/Helper/index.js";

const Customer = () => {
  const permission = getUserMenuPermission(6);
  const [isCustomerData, setIsCustomerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    customer_Id: "",
    customer_Name: "",
    firmName: "",
    mobile_No: "",
    address: "",
    gsT_No: "",
  });
  const userId = sessionStorage.getItem("userId");
  const fields = [
    {
      name: "customer_Name",
      label: "Customer Name",
      type: "text",
      required: true,
      errorMessage: "Customer name is required",
    },
    {
      name: "mobile_No",
      label: "ContactNo",
      type: "number",
      required: true,
      pattern: /^\d{10}$/,
      errorMessage: "10-digit contact number required",
    },
    { name: "firmName", label: "FirmName", type: "text" },
    {
      name: "address",
      label: "Address",
      type: "text",
      required: true,
      errorMessage: "Address name is required",
    },
    { name: "gsT_No", label: "GST No", type: "text" },
  ];
  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `Customer/GetAll/?pageNumber=${currentPage}&pageSize=${pageSize}`,
        {
          pageNumber: currentPage,
          pageSize,
        }
      );
      setIsCustomerData(res.data.customers);
      setFilteredData(res.data.customers);
      setTotalRecords(res.data.totalRecords);
      setTotalPages(
        res.data.customers.length < pageSize ? currentPage : currentPage + 1
      );
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, [currentPage]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddCustomer = () => {
    if (!permission.isInsert) {
      notify("You don't have permission to add Customer.", "error");
      return;
    }
    setFormData({
      customer_Id: "",
      customer_Name: "",
      firmName: "",
      mobile_No: "",
      address: "",
      gsT_No: "",
    });
    setIsEditing(false);
    setIsModelOpen(true);
  };

  const handleEditCustomer = async (id) => {
    try {
      const { data } = await axiosInstance.get(`Customer/GetById/${id}`);
      if (data) {
        setFormData({
          customer_Id: data.customer_Id || "",
          customer_Name: data.customer_Name || "",
          mobile_No: data.mobile_No || "",
          gsT_No: data.gsT_No || "",
          firmName: data.firm_Name || "",
          address: data.address || "",
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (err) {
      notify("Error fetching Customer data:", err);
    }
  };

  const handleDeleteCustomer = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`Customer/Delete/${selectedId}`);
      fetchCustomerData();
      notify("Customer deleted successfully!", "error");
    } catch (err) {
      notify("Failed to delete Customer!", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !permission.isInsert) return;
    if (isEditing && !permission.isUpdate) return;
    try {
      const payload = {
        Customer_Name: formData.customer_Name,
        Firm_Name: formData.firmName,
        Mobile_No: formData.mobile_No,
        Address: formData.address,
        GST_No: formData.gsT_No,
        Modified_By: userId,
      };

      if (isEditing) {
        payload.Customer_Id = formData.customer_Id;
      } else {
        payload.Created_By = userId;
      }

      await axiosInstance.post("Customer/SaveOrUpdate", payload);
      fetchCustomerData();
      setIsModelOpen(false);
      notify(
        isEditing
          ? "Customer updated successfully!"
          : "Customer added successfully!",
        "success"
      );
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };

  const fetchFilteredCustomer = async (query) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `Customer/FilterByName?customerName=${query}`
      );
      setFilteredData(res.data);
      setTotalRecords(res.data.length);
    } catch (err) {
      notify("Error fetching filtered data", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (searchQuery.trim() === "") {
      fetchCustomerData();
    } else {
      fetchFilteredCustomer(searchQuery);
    }
  }, [searchQuery, currentPage]);

  const clearSearch = () => {
    setSearchQuery("");
    fetchCustomerData();
  };
  return (
    <>
      <Toast />
      <div className="rounded-md bg-white shadow-md border px-5 pt-6 pb-2.5">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div className="relative w-full sm:w-1/2 lg:w-1/3">
            <input
              type="text"
              className="w-full bg-gray-100 border border-gray-3 py-2 pl-10 pr-10 rounded-md focus:ring-1 focus:ring-primary focus:border-primary outline-none"
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary">
              <LiaSearchPlusSolid className="w-5 h-5" />
            </button>
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-danger"
              >
                <MdClose className="w-5 h-5" />
              </button>
            )}
          </div>
          {permission.isInsert && (
            <button
              className="bg-primary rounded px-4 text-white py-2"
              onClick={handleAddCustomer}
            >
              + Add Customer
            </button>
          )}
        </div>

        {loading ? (
          <Loader />
        ) : permission.isView ? (
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full text-sm text-left border border-gray rounded-lg">
              <thead className="bg-primary text-white tracking-widest text-center font-light">
                <tr>
                  <th className="border border-gray-400 px-4 py-3">Name</th>
                  <th className="border border-gray-400 px-4 py-3">Contact</th>
                  <th className="border border-gray-400 px-4 py-3">GST NO</th>
                  <th className="border border-gray-400 px-4 py-3">Firm</th>
                  <th className="border border-gray-400 px-4 py-3 ">Address</th>
                  <th
                    className={`${
                      !permission.isUpdate && !permission.isDelete
                        ? "hidden"
                        : ""
                    } border border-gray-400 px-4 py-3 whitespace-nowrap`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="text-center">
                {filteredData?.length > 0 ? (
                  filteredData.map((customer) => (
                    <tr
                      key={customer.customer_Id}
                      className="border-b hover:bg-gray-50 transition cursor-pointer"
                    >
                      <td className="border border-gray-100 px-4 py-2">
                        {customer.customer_Name}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {customer.mobile_No}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {customer.gsT_No}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {customer.firm_Name}
                      </td>
                      <td className="border border-gray-100 px-4 py-2 max-w-[150px]">
                        {customer.address}
                      </td>

                      {(permission.isUpdate || permission.isDelete) && (
                        <td className="py-3 border border-gray-100 text-center space-x-2">
                          {permission.isDelete && (
                            <button
                              className="bg-red-100 text-red-600 hover:bg-red-200 px-2 py-1 rounded-md"
                              onClick={() =>
                                handleDeleteCustomer(customer.customer_Id)
                              }
                            >
                              <MdDeleteForever className="w-5 h-5" />
                            </button>
                          )}
                          {permission.isUpdate && (
                            <button
                              className="bg-[#e3f2fd] text-primary hover:bg-blue-200 px-2 py-1 rounded-md"
                              onClick={() =>
                                handleEditCustomer(customer.customer_Id)
                              }
                            >
                              <BiSolidEditAlt className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-red-500 text-lg py-10">
            You don't have permission to view this page.
          </div>
        )}

        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-6">
            Showing {isCustomerData.length + (currentPage - 1) * 10} of{" "}
            {totalRecords} records
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Customer" : "Add Customer"}
      >
        <FormFields
          onSubmit={handleSubmit}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          onClear={() => setIsModelOpen(false)}
          isEditing={isEditing}
        />
      </Model>
      <Model
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-4 text-center">
          <p className="text-lg font-semibold mb-4">
            Are you sure you want to delete this site?
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="bg-gray-5 px-4 py-2 rounded-md"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="bg-danger text-white px-4 py-2 rounded-md"
              onClick={handleConfirmDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default Customer;
